import React, { useState, useMemo } from "react";
import { Modal } from "../../../../../components";
import styles from "./CalendarModal.module.scss";
import arrowLeftIcon from "../../../../../assets/icons/arrowCalendarLeft.svg";
import arrowRightIcon from "../../../../../assets/icons/arrowCalendarRight.svg";

interface CalendarModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectDate: (date: string) => void;
  selectedDate: string;
}

export const CalendarModal: React.FC<CalendarModalProps> = ({
  visible,
  onClose,
  onSelectDate,
  selectedDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const days = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const daysInMonth = lastDay.getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daysArray: Array<{
      day: number | null;
      date: Date | null;
      isToday: boolean;
      isPast: boolean;
    }> = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      daysArray.push({ day: null, date: null, isToday: false, isPast: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;

      daysArray.push({
        day,
        date,
        isToday,
        isPast,
      });
    }

    return daysArray;
  }, [currentMonth]);

  const monthNames = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];

  const weekDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDayClick = (dayData: { day: number | null; date: Date | null; isPast: boolean }) => {
    if (!dayData.date || dayData.isPast) return;

    const formattedDate = dayData.date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    onSelectDate(formattedDate);
    onClose();
  };

  const isSelectedDate = (dayData: { date: Date | null }) => {
    if (!dayData.date) return false;
    const formattedDate = dayData.date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return formattedDate === selectedDate;
  };

  return (
    <Modal
      isOpen={visible}
      setIsOpen={onClose}
      title="Выберите дату доставки"
      modalWindowWidth={400}
    >
      <div className={styles.calendar}>
        <div className={styles.header}>
          <button className={styles.navButton} onClick={handlePrevMonth}>
            <img src={arrowLeftIcon} alt="Предыдущий месяц" />
          </button>
          <h3 className={styles.monthTitle}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button className={styles.navButton} onClick={handleNextMonth}>
            <img src={arrowRightIcon} alt="Следующий месяц" />
          </button>
        </div>

        <div className={styles.weekDays}>
          {weekDays.map((day) => (
            <div key={day} className={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>

        <div className={styles.days}>
          {days.map((dayData, index) => {
            if (dayData.day === null) {
              return <div key={`empty-${index}`} className={styles.dayEmpty} />;
            }

            return (
              <button
                key={`day-${dayData.day}`}
                className={`${styles.day} ${
                  dayData.isToday ? styles.dayToday : ""
                } ${dayData.isPast ? styles.dayPast : ""} ${
                  isSelectedDate(dayData) ? styles.daySelected : ""
                }`}
                onClick={() => handleDayClick(dayData)}
                disabled={dayData.isPast}
              >
                {dayData.day}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

